import { StrictMode, useEffect, useState, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { MotionConfig } from 'framer-motion'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import './combine.css'
import App from './App.tsx'

import RootErrorBoundary from './components/Common/RootErrorBoundary'
import { createTrustedTypesPolicy } from './utils/trustedTypes'

function MotionBootWrapper({ children }: { children: ReactNode }) {
  const [skipAnimations, setSkipAnimations] = useState(
    () =>
      typeof document !== 'undefined' && document.documentElement.hasAttribute('data-ssg')
  )

  useEffect(() => {
    if (!skipAnimations) return
    const id = requestAnimationFrame(() => setSkipAnimations(false))
    return () => cancelAnimationFrame(id)
  }, [skipAnimations])

  return (
    <MotionConfig skipAnimations={skipAnimations} reducedMotion="user">
      {children}
    </MotionConfig>
  )
}

// Initialize Trusted Types policy BEFORE React loads
// This must happen before any scripts are dynamically created
if (typeof window !== 'undefined') {
  createTrustedTypesPolicy()
}

// Note on chrome-extension://invalid console messages:
// If you see errors like "chrome-extension://invalid/" in the console,
// these are typically emitted by installed browser extensions (e.g., ad blockers,
// privacy tools, password managers) injecting content scripts, not by this app.
// They are harmless for the application logic. To verify, try an incognito window
// with extensions disabled, or a clean profile—the messages should disappear.

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Pick Pack Pro: missing #root element');
}

const isAdminPath =
  typeof window !== 'undefined' &&
  (window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/'));

if (isAdminPath) {
  rootEl.removeAttribute('data-ssg');
  document.documentElement.removeAttribute('data-ssg');
  document.documentElement.classList.remove('ppp-boot-loading');
  rootEl.replaceChildren();
} else if (rootEl.getAttribute('data-ssg') === 'true') {
  document.documentElement.setAttribute('data-ssg', 'true');
  document.documentElement.classList.remove('ppp-boot-loading');
}

try {
  const app = (
    <RootErrorBoundary>
      <HelmetProvider>
        <MotionBootWrapper>
          <App />
        </MotionBootWrapper>
      </HelmetProvider>
    </RootErrorBoundary>
  );

  createRoot(rootEl).render(import.meta.env.PROD ? app : <StrictMode>{app}</StrictMode>);
  requestAnimationFrame(() => {
    document.documentElement.classList.remove('ppp-boot-loading');
  });
} catch (e) {
  console.error(e);
  rootEl.innerHTML = `<div style="font-family:system-ui;padding:2rem;max-width:32rem;color:#0f172a">
    <p><strong>The site could not start.</strong> Please hard-refresh (Ctrl+F5) or try another browser.</p>
    <p style="color:#64748b;font-size:0.9rem">If you host this app, deploy the full <code>dist/</code> folder after <code>npm run build</code> — not the repository <code>index.html</code>.</p>
  </div>`;
}
